import { Grid, Stack } from '@mui/material';
import { MprcButton, MprcContainer } from 'components';
import { MprcButtonProps } from "components/MprcButton";
import { EnrollStatus, TrainingComponentType } from 'generated/CoreApi_v1';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router";
import { LmsPaths } from "routes/LmsRouter";
import { useEnrollmentViewContext } from 'views/lms/student/EnrollmentView';
import { getEnrollmentCertificate, launchSco } from 'views/lms/student/EnrollmentView/Shared/EnrollmentActions';
import { EnrollmentCloseButton } from 'views/lms/student/EnrollmentView/Shared/EnrollmentCloseButton';
import { EnrollmentStatus } from "views/lms/student/EnrollmentView/Shared/EnrollmentStatus";
import { ProgressResult } from "views/lms/student/EnrollmentView/Shared/ProgressResult";
import { ScoreResult } from "views/lms/student/EnrollmentView/Shared/ScoreResult";
import { TrainingAvatar } from "views/lms/student/EnrollmentView/Shared/TrainingAvatar";
import { TrainingDueDate } from "views/lms/student/EnrollmentView/Shared/TrainingDueDate";
import { TrainingInteractions } from "views/lms/student/EnrollmentView/Shared/TrainingInteractions";
import { TrainingRegistrationDialog } from 'views/lms/student/Registration/TrainingRegistrationDialog';

/////////////////////////////////////////////////////////////////////////////////////

export const ScoContentHeader: React.FC = () => {

    const { t } = useTranslation("lms");
    const navigate = useNavigate();

    const { enrollment, loading, onReload } = useEnrollmentViewContext();

    var currentComponent = enrollment.course?.components[0];

    const [dialog, setDialog] = useState<any>();

    /////////////////////////////////////////////////////////////////////////////////////

    const handleLaunchSco = useCallback(() => {
        launchSco(enrollment.enrollmentId, currentComponent!.scoComponent!, onReload, enrollment.readOnly);
    }, [enrollment, onReload]);

    ///
    const RenderActions = useMemo(() => {

        var actions: React.ReactNode[] = [];
        var principalActionProps: MprcButtonProps | null = null;
        var secondaryActionProps: MprcButtonProps | null = null;

        if (enrollment.finalizationDate === null) {

            if (enrollment.canFinish) {
                actions.push(<EnrollmentCloseButton disabled={loading} />);
            }

            if (currentComponent && currentComponent.componentType === TrainingComponentType.Sco) {
                if (currentComponent.scoComponent != undefined) {
                    if (enrollment.course!.singleComponent) {
                        principalActionProps = {
                            text: enrollment.canFinish && currentComponent.scoComponent.lessonStatus != null ? t("Student.EnrollmentView.ScoReviewButtonText") : t("Student.EnrollmentView.ScoStartSingleButtonText"),
                            onClick: handleLaunchSco
                        };
                    }
                    else {
                        const lessonStatus = currentComponent.scoComponent.lessonStatus;
                        if (lessonStatus == null || lessonStatus === "not attempted") {
                            principalActionProps = {
                                text: t("Student.EnrollmentView.ScoStartButtonText"),
                                onClick: handleLaunchSco
                            };
                        }
                        else if (lessonStatus === "incomplete") {
                            principalActionProps = {
                                text: t("Student.EnrollmentView.ScoContinueButtonText"),
                                onClick: handleLaunchSco
                            };
                        }
                        else {
                            principalActionProps = {
                                text: enrollment.canFinish ? t("Student.EnrollmentView.ScoReviewButtonText") : `${t("Student.EnrollmentView.ScoReviewButtonText")} / ${t("Student.EnrollmentView.ScoRemakeButtonText")}`,
                                onClick: handleLaunchSco
                            };
                        }
                    }
                }
            }
        }
        else {
            const hasCertificate = enrollment.hasCertificate && enrollment.certificateId != null;
            const canReenroll = enrollment.canReEnroll;
            const canReviewSco = currentComponent && currentComponent.componentType === TrainingComponentType.Sco && currentComponent.scoComponent?.lessonStatus != null;

            if (hasCertificate) {
                principalActionProps = {
                    text: t(canReviewSco ? "Student.EnrollmentView.CertificateShortButtonText" : "Student.EnrollmentView.CertificateButtonText"),
                    onClick: () => getEnrollmentCertificate(enrollment.certificateId!)
                };
            } else if (canReenroll && !canReviewSco) {
                principalActionProps = {
                    text: t("Student.EnrollmentView.ReEnrollButtonText"),
                    onClick: () => {
                        setDialog(
                            <TrainingRegistrationDialog
                                trainingId={enrollment.trainingId}
                                onClose={(result) => {
                                    if (result?.enrollmentId) {
                                        navigate(LmsPaths.BuildEnrollmentViewer(enrollment.trainingId, result.enrollmentId));
                                    }
                                }}
                            />);
                    }
                };
            }

            if (canReviewSco) {
                secondaryActionProps = {
                    text: t("Student.EnrollmentView.ScoReviewButtonText"),
                    onClick: handleLaunchSco
                };
            }
        }

        if (principalActionProps != null) {
            actions.push(
                <MprcButton
                    key="principalAction"
                    fullWidth
                    variant="contained"
                    title={principalActionProps.title ?? principalActionProps.text}
                    {...principalActionProps} />
            );
        }

        if (secondaryActionProps != null) {
            actions.push(
                <MprcButton
                    key="secondaryAction"
                    fullWidth
                    variant="contained"
                    title={secondaryActionProps.title ?? secondaryActionProps.text}
                    {...secondaryActionProps} />
            );
        }

        return (
            <>
                {
                    actions.length > 0 &&
                    <Grid sx={{ width: 220 }}>
                        <Stack direction="row" spacing={1}>
                            {actions}
                        </Stack>
                    </Grid>
                }
            </>
        );
    }, [enrollment, currentComponent, t, loading]);

    //////////////////////////////////////////////////////
    /// RENDER
    //////////////////////////////////////////////////////
    return (
        <>
            {dialog}

            <Grid container spacing={2} width="100%">
                <Grid size={{ xs: 12 }} mt={1}>
                    <Grid container spacing={2} wrap="nowrap" width="100%">
                        <Grid>
                            <TrainingAvatar />
                        </Grid>
                        <Grid size="grow">
                            <MprcContainer>
                                {
                                    enrollment.status == EnrollStatus.NotStarted || enrollment.status == EnrollStatus.InProgress
                                        ? <TrainingDueDate enrollment={enrollment} />
                                        : <EnrollmentStatus variant="block" data={enrollment} />
                                }
                            </MprcContainer>
                            {!enrollment.isPreview &&
                                <MprcContainer>
                                    <Grid container spacing={8} width="100%">
                                        {
                                            !enrollment.course!.singleComponent &&
                                            <Grid>
                                                <ProgressResult
                                                    variant={"block"}
                                                    data={{
                                                        enrollmentId: enrollment.enrollmentId,
                                                        progress: enrollment.progressPercent,
                                                        minProgress: enrollment.minProgress ?? 0,
                                                        status: enrollment.status,
                                                        isClosed: enrollment.finalizationDate != null,
                                                        totalComponents: enrollment.totalComputeProgressComponents,
                                                        totalCompletedComponents: enrollment.totalCompletedComputeProgressComponents,
                                                    }}
                                                    enableShowDetails
                                                />
                                            </Grid>
                                        }
                                        {
                                            enrollment.hasScore &&
                                            <Grid>
                                                <ScoreResult
                                                    variant={enrollment.course!.singleComponent ? "single" : "multiple"}
                                                    enrollmentId={enrollment.enrollmentId}
                                                    score={enrollment.scorePercent}
                                                    minScore={enrollment.minFinalScore ?? 0}
                                                    isClosed={enrollment.finalizationDate != null}
                                                    totalItems={enrollment.totalScoredComponents}
                                                    totalCompletedItems={enrollment.totalCompletedScoredComponents}
                                                    status={enrollment.status}
                                                    enableShowDetails
                                                />
                                            </Grid>
                                        }
                                    </Grid>
                                </MprcContainer>
                            }
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2} width="100%">
                        {RenderActions}
                        <Grid size="grow">
                            <TrainingInteractions enrollment={enrollment} readOnly={enrollment.isPreview} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};
